import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc as docRef
} from 'firebase/firestore';
import { db } from '../firebase';

export default function CreateAttendance({
  batchId,
  date: propDate = new Date().toISOString().slice(0,10),
  topic: propTopic = '',
  isEditMode = false,
  onBack = () => {}
}) {
  const [students, setStudents] = useState([]);
  const [date, setDate]         = useState(propDate);
  const [topic, setTopic]       = useState(propTopic);
  const [records, setRecords]   = useState({});
  const [message, setMessage]   = useState('');

  useEffect(() => {
    if (!batchId) return;
    (async () => {
      const stuSnap = await getDocs(collection(db,'students'));
      const list = stuSnap.docs
        .map(d=>({ id:d.id, ...d.data() }))
        .filter(s => (s.batch_ids||[]).includes(batchId));
      setStudents(list);

      const recs = {};
      if (isEditMode) {
        const attSnap = await getDocs(
          query(
            collection(db,'attendance'),
            where('batch_id','==',batchId),
            where('date','==',propDate),
            where('topic','==',propTopic)
          )
        );
        attSnap.docs.forEach(d => {
          const dData = d.data();
          recs[dData.student_id] = {
            id: d.id,
            status: dData.status,
            reason: dData.reason || ''
          };
        });
      }
      list.forEach(s => {
        if (!recs[s.id]) recs[s.id] = { status:'present_offline', reason:'' };
      });
      setRecords(recs);
    })();
  }, [batchId, isEditMode, propDate, propTopic]);

  const handleStatus = (sid, status) => {
    setRecords(r => ({ ...r, [sid]: { ...r[sid], status } }));
  };
  const handleReason = (sid, reason) => {
    setRecords(r => ({ ...r, [sid]: { ...r[sid], reason } }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMessage('');
    try {
      await Promise.all(students.map(s => {
        const rec = records[s.id];
        if (isEditMode && rec.id) {
          return updateDoc(docRef(db,'attendance',rec.id), {
            status: rec.status,
            reason: rec.status==='absent' ? rec.reason : ''
          });
        } else {
          return addDoc(collection(db,'attendance'), {
            batch_id:   batchId,
            date,
            topic,
            student_id: s.id,
            status:     rec.status,
            reason:     rec.status==='absent' ? rec.reason : ''
          });
        }
      }));
      setMessage(isEditMode ? 'Attendance updated.' : 'Attendance recorded.');
      if (!isEditMode) setTopic('');
    } catch (err) {
      console.error(err);
      setMessage('Error: ' + err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
      <h2 className="text-xl font-semibold">
        {isEditMode ? 'Edit Attendance' : 'Create Attendance'}
      </h2>

      {/* Date & Topic */}
      <div className="flex gap-4">
        <div>
          <label className="block">Date</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            disabled={isEditMode}
            className="px-3 py-2 border rounded"
          />
        </div>
        <div>
          <label className="block">Topic</label>
          <input
            type="text"
            value={topic}
            onChange={e => setTopic(e.target.value)}
            required
            disabled={isEditMode}
            className="w-full px-3 py-2 border rounded"
          />
        </div>
      </div>

      {/* Attendance Table */}
      <div className="max-h-64 overflow-auto border rounded">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Student</th>
              <th className="p-2 text-center">Offline</th>
              <th className="p-2 text-center">Online</th>
              <th className="p-2 text-center">Absent</th>
              <th className="p-2 text-left">Reason</th>
            </tr>
          </thead>
          <tbody>
            {students.map(s => (
              <tr key={s.id} className="odd:bg-white even:bg-gray-50">
                <td className="p-2">{s.name}</td>
                {['present_offline','present_online','absent'].map(stat => (
                  <td key={stat} className="p-2 text-center">
                    <input
                      type="radio"
                      name={`att-${s.id}`}
                      checked={records[s.id]?.status === stat}
                      onChange={()=>handleStatus(s.id, stat)}
                    />
                  </td>
                ))}
                <td className="p-2">
                  <input
                    type="text"
                    placeholder="Reason"
                    value={records[s.id]?.reason}
                    disabled={records[s.id]?.status !== 'absent'}
                    onChange={e=>handleReason(s.id, e.target.value)}
                    className="px-2 py-1 border rounded w-full"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Submit + Back to List */}
      <div className="flex items-center gap-4">
        <button
          type="submit"
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          {isEditMode ? 'Save Changes' : 'Submit Attendance'}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
        >
          Back to List
        </button>
      </div>

      {message && <p className="text-sm mt-2">{message}</p>}
    </form>
  );
}
